// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Magazzino prodotti finiti e bolle di uscita"
// Centro assistenza → guida per operatori e amministratori, solo piano TRACE.
//
// L'ultimo anello: il lotto prodotto e il cliente a cui e' andato. E' la coppia
// di schermate che serve il giorno del richiamo, ed e' il motivo per cui tutto
// il resto della tracciabilita' esiste.
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Etichette copiate dal
// codice.
//
// Riferimenti nel codice, per chi dovra' aggiornarla:
//   Magazzino PF ....... App.js, GestioneMagazzinoPF
//     Stati ............ STATO_PF_LABEL: DISPONIBILE | IN_MAGAZZINO | PARZIALE
//                        | ESAURITO | IN_USO. I semilavorati usano
//                        STATO_SL_LABEL e non hanno PARZIALE.
//     Filtri ........... Stato · Tipo (Tutti / Solo PF / Solo SL) · Prodotto · Cerca
//     Residuo .......... quantita − somma delle righe di bolla uscita
//     Rettifica ........ POST /magazzino-pf/:id/rettifica, motivi in
//                        MOTIVO_RETTIFICA_LABEL. Note obbligatorie se ALTRO.
//                        quantitaDopo = 0 → ESAURITO.
//   Bolle uscita ....... App.js, GestioneBolleUscita
//     Il selettore lotto carica solo categoriaProdotto=PRODOTTO_FINITO e
//     scarta gli ESAURITO: i SEMILAVORATI non sono spedibili da qui.
//     POST /bolle-uscita e' TRANSAZIONALE con FOR UPDATE sul lotto. Rifiuta con
//     "Lotto X: quantità in uscita (N) superiore al residuo disponibile (N um)".
//     Dopo l'inserimento lo stato del lotto diventa PARZIALE o ESAURITO da solo.
//   NIENTE STAMPA ...... GestioneBolleUscita non ha nessuna funzione di stampa,
//                        ed e' una scelta confermata da Manuele il 18/08/2026:
//                        serve il numero per legare la tracciabilita', il
//                        documento fiscale non lo gestiamo. Il capitolo 6 e'
//                        scritto per non invecchiare male — dice che il DDT
//                        vive dove vive la numerazione fiscale, il che resta
//                        vero anche se un domani quella numerazione entra in
//                        LabThree col blocco MASTER. SE MASTER arrivera' a
//                        emettere DDT, il capitolo 6 va riscritto.
//                        Idea aperta e non decisa: allegare il PDF della bolla
//                        (vedi HANDOFF.md, sezione "Documenti di trasporto").
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, Passo,
  MockFrame, Schermo, SchermoTitolo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'u0', label: 'Perché conta più di tutto' },
  { id: 'u1', label: '1. Il magazzino prodotti finiti' },
  { id: 'u2', label: '2. Prodotto o semilavorato' },
  { id: 'u3', label: '3. Prodotta, spedita, residua' },
  { id: 'u4', label: '4. Registrare una spedizione' },
  { id: 'u5', label: '5. Rettificare una quantità' },
  { id: 'u6', label: '6. E il DDT da consegnare?' },
  { id: 'u7', label: 'Le trappole' },
  { id: 'u8', label: 'Domande frequenti' },
];

// ─── Pezzi di schermata ──────────────────────────────────────────────────────

const ChipFinto = ({ children, tono = 'neutro' }) => {
  const c = {
    verde:   { fg: LT.verde,   bg: LT.verdeSoft },
    rosso:   { fg: LT.rosso,   bg: LT.rossoSoft },
    arancio: { fg: LT.arancio, bg: LT.arancioSoft },
    blu:     { fg: LT.blu,     bg: LT.bluSoft },
    viola:   { fg: LT.viola,   bg: LT.violaSoft },
    ciano:   { fg: LT.ciano,   bg: LT.cianoSoft },
    neutro:  { fg: LT.n700,    bg: LT.n100 },
  }[tono];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 999, background: c.bg, color: c.fg,
      fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const BtnFinto = ({ children, kind = 'neutro' }) => {
  const c = {
    viola:  { bg: LT.viola, fg: '#fff',  bd: LT.viola },
    blu:    { bg: LT.blu,   fg: '#fff',  bd: LT.blu },
    neutro: { bg: LT.n0,    fg: LT.n700, bd: LT.n300 },
  }[kind];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      padding: '5px 10px', borderRadius: LT.r.md, fontSize: 12, fontWeight: 600,
      background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const CampoFinto = ({ etichetta, valore, aiuto, larghezza }) => (
  <div style={{ width: larghezza || '100%' }}>
    <div style={{
      fontSize: 10.5, fontWeight: 700, color: LT.n600,
      textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 3,
    }}>{etichetta}</div>
    <div style={{
      border: `1px solid ${LT.n300}`, borderRadius: LT.r.md, padding: '7px 9px',
      background: LT.n0, fontSize: 13, color: valore ? LT.n900 : LT.n400,
    }}>{valore || '—'}</div>
    {aiuto && (
      <div style={{ fontSize: 10.5, color: LT.n500, marginTop: 3, lineHeight: 1.45 }}>{aiuto}</div>
    )}
  </div>
);

const DatoPF = ({ etichetta, valore, colore }) => (
  <div>
    <div style={{
      fontSize: 9.5, color: LT.n600, textTransform: 'uppercase',
      letterSpacing: 0.3, fontWeight: 700,
    }}>{etichetta}</div>
    <div style={{ fontSize: 12, color: colore || LT.navy, fontWeight: 700, marginTop: 2 }}>
      {valore}
    </div>
  </div>
);

const CardPF = ({ bordo, icona, prodotto, lotto, chips, dati, azioni }) => (
  <div style={{
    display: 'flex', background: LT.n0, border: `1px solid ${LT.n200}`,
    borderRadius: LT.r.md, overflow: 'hidden', marginBottom: 10,
  }}>
    <div style={{ width: 5, alignSelf: 'stretch', background: bordo }} />
    <div style={{ flex: 1, padding: 12, minWidth: 0 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', gap: 10, marginBottom: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
          <div style={{
            width: 34, height: 34, borderRadius: LT.r.md,
            background: bordo === LT.ciano ? LT.cianoSoft : LT.violaSoft,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={icona} size={18} stroke={bordo} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: LT.navy }}>{prodotto}</div>
            <div style={{ fontSize: 11.5, color: LT.n600, fontFamily: LT.mono, marginTop: 2 }}>{lotto}</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>{chips}</div>
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(84px, 1fr))',
        gap: 9, padding: '8px 11px', background: LT.n50,
        borderRadius: LT.r.md, marginBottom: 9,
      }}>{dati}</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{azioni}</div>
    </div>
  </div>
);

export default function ProdottiFinitiUscite() {
  return (
    <GuidaLayout
      titolo="Magazzino prodotti finiti e bolle di uscita"
      sottotitolo="Il lotto che avete prodotto e il cliente a cui è andato. Sono le due schermate che rispondono alla domanda per cui esiste tutto il resto: se questo lotto ha un problema, chi devo chiamare?"
      durata="~ 15 minuti"
      destinatario="Per operatori e amministratori · piano TRACE"
      sezioni={SEZIONI}
    >

      {/* u0 */}
      <H id="u0">Perché conta più di tutto</H>

      <P>
        Tutta la tracciabilità serve a chiudere un anello. Le bolle di ingresso dicono da dove
        arriva la materia prima, le lavorazioni dicono cosa ne avete fatto, e{' '}
        <Strong>le bolle di uscita dicono dove è andato il risultato</Strong>.
      </P>
      <P>
        Se manca quest'ultimo pezzo, tutto il lavoro fatto prima non serve nel momento in cui
        serve davvero. Sapere che il lotto <Code>L.120826</Code> conteneva la carne del macello X
        è inutile se poi nessuno sa a quali sei clienti quel lotto è stato spedito.
      </P>

      <Callout kind="info" titolo="Il richiamo si gioca qui">
        Il Regolamento 178/2002 chiede di sapere <em>a chi</em> avete fornito, non solo{' '}
        <em>da chi</em> avete ricevuto. Le bolle di uscita sono la metà del "one step forward",
        e sono l'unica metà che dipende interamente dalla vostra disciplina quotidiana: nessuno
        ve la ricorda, il camion parte lo stesso.
      </Callout>

      {/* u1 */}
      <H id="u1" cat="produzione">1. Il magazzino prodotti finiti</H>

      <P>
        <Code>Magazzino prodotti finiti</Code>, nel gruppo Uscite, elenca i lotti nati dalla{' '}
        <Strong>chiusura delle lavorazioni</Strong>. Non si aggiunge niente a mano: un lotto
        compare qui perché una lavorazione è stata chiusa, punto.
      </P>

      <MockFrame
        url="app.labthree.it — Magazzino prodotti finiti"
        markers={[
          { n: 1, top: '30%', left: '92%', titolo: 'Il tipo',
            testo: 'La sigla SL distingue un semilavorato da un prodotto finito. Cambia cosa potete farci.' },
          { n: 2, top: '52%', left: '38%', titolo: 'I tre numeri',
            testo: 'Prodotta, Spedito, Residuo. Il residuo è quello che potete ancora vendere.' },
          { n: 3, top: '70%', left: '18%', titolo: 'Bolla uscita',
            testo: 'Apre la bolla con il lotto già selezionato. È la via più corta per spedire.' },
        ]}
      >
        <Schermo>
          <SchermoTitolo
            titolo="Magazzino prodotti finiti"
            sottotitolo="I lotti nati dalla chiusura delle lavorazioni"
          />

          <CardPF
            bordo={LT.viola}
            icona={I.package}
            prodotto="Prosciutto crudo stagionato"
            lotto="L. 23 PT"
            chips={<ChipFinto tono="verde">Parziale</ChipFinto>}
            dati={
              <>
                <DatoPF etichetta="Prodotta" valore="154 pz" />
                <DatoPF etichetta="Residuo" valore="112 pz" colore={LT.verde} />
                <DatoPF etichetta="Spedito" valore="42 pz" colore={LT.blu} />
                <DatoPF etichetta="Data" valore="12/06/2026" />
              </>
            }
            azioni={
              <>
                <BtnFinto kind="viola">Bolla uscita</BtnFinto>
                <BtnFinto>Rettifica</BtnFinto>
                <BtnFinto>Storico</BtnFinto>
              </>
            }
          />

          <CardPF
            bordo={LT.ciano}
            icona={I.flask}
            prodotto="Impasto base salame"
            lotto="L.180826"
            chips={
              <>
                <ChipFinto tono="ciano">SL</ChipFinto>
                <ChipFinto tono="verde">In uso</ChipFinto>
              </>
            }
            dati={
              <>
                <DatoPF etichetta="Prodotta" valore="240 kg" />
                <DatoPF etichetta="Data" valore="18/08/2026" />
              </>
            }
            azioni={
              <>
                <BtnFinto>Esaurito</BtnFinto>
                <BtnFinto>Rettifica</BtnFinto>
                <BtnFinto>Storico</BtnFinto>
              </>
            }
          />
        </Schermo>
      </MockFrame>

      <P>
        Quattro filtri: <Tasto>Stato</Tasto>, <Tasto>Tipo</Tasto> con{' '}
        <Tasto>Solo PF</Tasto> / <Tasto>Solo SL</Tasto>, <Tasto>Prodotto</Tasto> e{' '}
        <Tasto>Cerca</Tasto>, che guarda lotto, nome del prodotto e committente.
      </P>

      {/* u2 */}
      <H id="u2" cat="produzione">2. Prodotto finito o semilavorato</H>

      <P>
        La differenza si vede dalla sigla <ChipFinto tono="ciano">SL</ChipFinto> e dal bordo
        azzurro, e cambia cosa la schermata vi lascia fare.
      </P>

      <Passo n="1" titolo="Un prodotto finito si spedisce">
        <P style={{ margin: '4px 0' }}>
          Ha il pulsante <Tasto>Bolla uscita</Tasto>, e i tre numeri Prodotta / Residuo /
          Spedito. Passa da <Strong>In magazzino</Strong> a <Strong>Parziale</Strong> a{' '}
          <Strong>Esaurito</Strong> man mano che esce.
        </P>
      </Passo>

      <Passo n="2" titolo="Un semilavorato si consuma">
        <P style={{ margin: '4px 0' }}>
          Non ha la bolla di uscita: torna dentro un'altra lavorazione come ingrediente. I
          pulsanti sono <Tasto>Metti in uso</Tasto> e <Tasto>Esaurito</Tasto>, come per una
          materia prima. Non ha lo stato Parziale, perché non si misura in spedizioni.
        </P>
      </Passo>

      <Callout kind="warn" titolo="Un semilavorato non è spedibile da qui">
        Nel selettore della bolla di uscita compaiono solo i prodotti finiti. Se dovete vendere
        qualcosa che avete classificato come semilavorato — capita: l'impasto venduto sfuso a un
        collega — la classificazione è sbagliata e va corretta in anagrafica prodotti. È una
        scelta di catalogo, non un limite della bolla.
      </Callout>

      {/* u3 */}
      <H id="u3" cat="produzione">3. Prodotta, spedita, residua</H>

      <P>Tre numeri che vanno letti insieme, perché uno solo non dice niente.</P>

      <div style={{
        border: `1px solid ${LT.n200}`, borderRadius: LT.r.md,
        padding: '14px 18px', margin: '18px 0', background: LT.n0,
      }}>
        {[
          ['Prodotta', 'Quanto è uscito dalla lavorazione. Si muove solo con una rettifica.'],
          ['Spedito', 'La somma di tutte le righe di bolla di uscita su quel lotto.'],
          ['Residuo', 'Prodotta meno Spedito. È quello che potete ancora vendere, ed è il numero che il programma controlla.'],
        ].map(([n, d], i) => (
          <div key={n} style={{
            display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap',
            padding: '9px 0', borderBottom: i < 2 ? `1px solid ${LT.n150}` : 'none',
          }}>
            <span style={{ flex: '0 0 90px', fontSize: 14, fontWeight: 700, color: LT.navy }}>{n}</span>
            <span style={{ flex: '1 1 260px', fontSize: 14, color: LT.n700, lineHeight: 1.55 }}>{d}</span>
          </div>
        ))}
      </div>

      <P>
        Lo stato si aggiorna da solo a ogni spedizione: finché resta qualcosa il lotto è{' '}
        <ChipFinto tono="verde">Parziale</ChipFinto>, quando il residuo arriva a zero diventa{' '}
        <ChipFinto>Esaurito</ChipFinto>. Non c'è niente da cliccare.
      </P>

      <Callout kind="info" titolo="Qui il controllo c'è davvero">
        È l'unico punto della tracciabilità dove il programma vi ferma su una quantità. Nel
        magazzino materie prime i consumi sono stime e nessuno vi blocca; qui il residuo è un
        conto esatto — quanto avete prodotto meno quanto avete già spedito — e spedire più di
        quello vorrebbe dire aver venduto due volte la stessa merce.
      </Callout>

      {/* u4 */}
      <H id="u4" cat="temperature">4. Registrare una spedizione</H>

      <P>
        Due strade, stesso risultato. Da <Code>Magazzino prodotti finiti</Code> il pulsante{' '}
        <Tasto>Bolla uscita</Tasto> sul lotto apre la bolla con quel lotto già dentro. Da{' '}
        <Code>Bolle di uscita</Code> si parte in bianco con <Tasto>Nuova bolla uscita</Tasto> e
        si scelgono i lotti a mano: è la strada giusta quando una spedizione ne contiene più
        d'uno.
      </P>

      <MockFrame
        url="app.labthree.it — Nuova bolla di uscita"
        markers={[
          { n: 1, top: '30%', left: '50%', titolo: 'Cliente',
            testo: 'Obbligatorio, ed è il dato che rende utile tutta la bolla. Se manca in anagrafica si crea qui.' },
          { n: 2, top: '68%', left: '30%', titolo: 'Lotto da spedire',
            testo: 'Solo prodotti finiti non esauriti. Sotto il campo compare il residuo disponibile.' },
          { n: 3, top: '68%', left: '72%', titolo: 'Quantità',
            testo: 'Non può superare il residuo. Il programma rifiuta l’intera bolla, non solo la riga.' },
        ]}
      >
        <Schermo>
          <SchermoTitolo titolo="Nuova bolla uscita" sottotitolo="Dati bolla" />
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <CampoFinto etichetta="Numero bolla" valore="2026/1180" larghezza="30%" />
            <CampoFinto etichetta="Data bolla *" valore="18/08/2026" larghezza="30%" />
            <CampoFinto etichetta="Cliente *" valore="Salumeria Rossi Srl" larghezza="34%" />
          </div>

          <div style={{
            fontSize: 12.5, fontWeight: 800, color: LT.navy,
            marginTop: 14, marginBottom: 2,
          }}>Prodotti spediti</div>
          <div style={{ fontSize: 11, color: LT.n500, marginBottom: 9 }}>
            Aggiungi le righe con i lotti di PF da spedire e le quantità
          </div>

          <div style={{
            background: LT.violaSoft, border: `1px solid ${LT.viola}30`,
            borderRadius: LT.r.md, padding: 10,
          }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 210px' }}>
                <CampoFinto etichetta="Lotto da spedire *" valore="Prosciutto crudo — L. 23 PT" />
                <div style={{ fontSize: 10.5, color: LT.verde, fontWeight: 700, marginTop: 3 }}>
                  Disponibile: 112 pz
                </div>
              </div>
              <div style={{ flex: '0 0 100px' }}>
                <CampoFinto etichetta="Quantità *" valore="40" />
              </div>
              <div style={{ flex: '0 0 80px' }}>
                <CampoFinto etichetta="Unità" valore="pz" />
              </div>
            </div>
          </div>
        </Schermo>
      </MockFrame>

      <P>
        Il cliente è obbligatorio ed è il motivo per cui la bolla esiste. Se non è in anagrafica
        si crea al volo dallo stesso form, con la sola ragione sociale — poi lo completate con
        calma. Meglio un cliente incompleto che una spedizione non registrata.
      </P>

      <Callout kind="warn" titolo="La bolla si salva tutta o niente">
        Se una riga sfora il residuo, il programma rifiuta <Strong>l'intera bolla</Strong> e vi
        dice quale lotto e di quanto: <em>"Lotto L. 23 PT: quantità in uscita (140) superiore al
        residuo disponibile (112 pz)"</em>. Non ne salva metà. È voluto: mezza bolla registrata
        sarebbe peggio di nessuna bolla, perché sembrerebbe completa.
      </Callout>

      <Callout kind="tip" titolo="Una bolla, tanti lotti">
        Se al cliente mandate tre prodotti diversi, fate <Strong>una bolla con tre righe</Strong>,
        non tre bolle. Corrisponde al DDT vero e, il giorno del richiamo, mostra in una schermata
        tutto quello che quel cliente ha ricevuto in quella consegna.
      </Callout>

      {/* u5 */}
      <H id="u5" cat="pulizie">5. Rettificare una quantità</H>

      <P>
        <Tasto>Rettifica</Tasto> serve quando la quantità prodotta non è più quella: un calo di
        peso in stagionatura, del prodotto rotto, un inventario che non torna. Si scrive la{' '}
        <Strong>nuova quantità</Strong> — non la differenza — e si sceglie un motivo fra{' '}
        <Tasto>Scarto/scadenza</Tasto>, <Tasto>Calo/sfrido lavorazione</Tasto>,{' '}
        <Tasto>Rottura/danneggiamento</Tasto>, <Tasto>Rettifica inventariale</Tasto> e{' '}
        <Tasto>Altro</Tasto>. Con "Altro" le note diventano obbligatorie.
      </P>
      <P>
        Ogni rettifica resta scritta con quantità prima, quantità dopo, motivo, note e chi l'ha
        fatta. Si rilegge tutto da <Tasto>Storico</Tasto>. Portare la quantità a zero marca il
        lotto <Strong>Esaurito</Strong>.
      </P>

      <Callout kind="info" titolo="Perché il calo peso è una rettifica e non un errore">
        Un prosciutto che entra a 12 kg e esce a 9 non è un ammanco: è quello che fa un
        prosciutto. Registrarlo come <Strong>Calo/sfrido lavorazione</Strong> spiega la
        differenza nel bilancio di massa, che altrimenti la segnalerebbe come uno scostamento
        critico. È il modo di dire al programma "lo so, è normale".
      </Callout>

      {/* u6 */}
      <H id="u6" cat="temperature">6. E il DDT da consegnare al cliente?</H>

      <P>
        Lo emette il vostro gestionale o il commercialista, come sempre. LabThree{' '}
        <Strong>non stampa il documento di trasporto</Strong>, e non è una dimenticanza: il DDT
        è un documento fiscale, e un documento fiscale vive dove vive la sua numerazione. Finché
        quella sta nel vostro gestionale, il DDT lo emette il gestionale — due numerazioni in due
        programmi diversi prima o poi divergono, e la vostra sarebbe quella sbagliata.
      </P>
      <P>
        Quello che LabThree registra è l'altra metà: <Strong>il collegamento fra il lotto e il
        cliente</Strong>. Il campo <Tasto>Numero bolla</Tasto> serve proprio a legare le due
        cose — ci scrivete il numero del DDT vero, e da lì in avanti i due sistemi si parlano.
      </P>

      <Callout kind="tip" titolo="Il documento che LabThree produce è un altro">
        La prova che serve a un'ispezione o a un richiamo non è la copia del DDT: è il{' '}
        <Strong>test di tracciabilità</Strong>, che da un lotto ricostruisce a valle tutti i
        clienti che l'hanno ricevuto e a monte tutte le materie prime che ci sono entrate. Quello
        si stampa, e ha una guida dedicata.
      </Callout>

      {/* u7 */}
      <H id="u7">Le trappole</H>

      <Callout kind="warn" titolo="Registrare la bolla il giorno dopo è la vera trappola">
        Il camion parte comunque, e nessuno vi blocca. Le uscite non registrate sono il buco più
        comune della rintracciabilità, e si scoprono sempre nel momento peggiore. Un momento
        fisso nella giornata — a fine spedizioni, prima di chiudere — vale più di qualunque
        funzione del programma.
      </Callout>

      <Callout kind="warn" titolo="Il lotto compare in magazzino solo dopo la chiusura">
        Se avete appena finito di produrre e non trovate il lotto, quasi sempre la lavorazione è
        ancora <Strong>Aperta</Strong>. Il lotto di prodotto finito nasce alla chiusura, non
        prima.
      </Callout>

      <Callout kind="warn" titolo="Un lotto esaurito sparisce dal selettore">
        Nella bolla di uscita compaiono solo i lotti con residuo. Se un lotto vi serve ancora ed
        è già a zero, il conto è sbagliato da qualche parte: guardate lo <Tasto>Storico</Tasto>{' '}
        e le bolle già registrate su quel lotto prima di rettificare.
      </Callout>

      <Callout kind="warn" titolo="La quantità prodotta non si corregge da qui a mano">
        Passa sempre da <Tasto>Rettifica</Tasto>, con un motivo. È una scomodità voluta: una
        quantità che cambia senza spiegazione è esattamente quello che un ispettore cerca.
      </Callout>

      {/* u8 */}
      <H id="u8">Domande frequenti</H>

      <div style={{ marginTop: 8 }}>
        <FAQ q="Ho spedito a un cliente che non è in anagrafica.">
          Si crea dentro il form della bolla: serve solo la ragione sociale. Completatelo poi con
          indirizzo e contatti — che però in un richiamo servono davvero, quindi meglio prima
          che poi.
        </FAQ>

        <FAQ q="Ho sbagliato la quantità su una bolla già salvata.">
          Dell'intestazione si corregge numero, data, cliente e note. Le righe no. Se la
          quantità è sbagliata, la strada pulita è una bolla nuova per la differenza, oppure una
          rettifica sul lotto con motivo <Strong>Rettifica inventariale</Strong> e una nota che
          spieghi. In tutti e due i casi resta la traccia dell'errore, che è quello che serve.
        </FAQ>

        <FAQ q="Il cliente mi ha reso della merce. Come la registro?">
          Non c'è un reso vero e proprio. Nella pratica si registra una{' '}
          <Strong>rettifica</Strong> sul lotto, riportando su la quantità e scrivendo nelle note
          il cliente e il motivo del reso. Se la merce è tornata perché aveva un problema, la
          cosa importante è aprire una <Strong>non conformità</Strong>: è lì che quella storia
          deve stare.
        </FAQ>

        <FAQ q="Devo registrare anche i campioni e gli omaggi?">
          Sì, se il prodotto esce dallo stabilimento ed è destinato a essere mangiato. Un
          campione a una fiera è a tutti gli effetti una fornitura, e in un richiamo va
          rintracciato come gli altri. Basta una bolla senza numero, con il destinatario e la
          quantità.
        </FAQ>

        <FAQ q="Posso vedere tutte le spedizioni di un lotto?">
          Sì. Nell'elenco delle bolle di uscita la ricerca guarda anche prodotto e lotto: si
          scrive il lotto e restano solo le bolle che lo contengono. La ricostruzione completa,
          con anche le materie prime a monte, la fa il test di tracciabilità.
        </FAQ>

        <FAQ q="Chi può registrare una bolla di uscita?">
          Chiunque abbia accesso al modulo, operatori compresi. È un gesto di spedizione e va
          fatto da chi carica il camion, non a fine mese dall'ufficio.
        </FAQ>

        <FAQ q="Il prodotto è di un committente. La bolla di uscita va bene lo stesso?">
          Sì: la restituzione al committente è una bolla di uscita come le altre, e il
          committente si seleziona come cliente. Il lotto porta il contrassegno{' '}
          <ChipFinto tono="arancio">Conto terzi</ChipFinto>, così sapete che quella merce non
          l'avete venduta: l'avete resa.
        </FAQ>
      </div>

      <div style={{
        marginTop: 40, padding: '18px 20px', background: LT.bluSoft,
        borderRadius: LT.r.md, display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
        <Ico d={I.arrowRight} size={20} stroke={LT.blu} style={{ marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 700, color: LT.blu, fontSize: 14, marginBottom: 4 }}>
            La guida dopo questa
          </div>
          <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.6 }}>
            <Strong>Il test di tracciabilità</Strong>: la simulazione di richiamo a monte e a
            valle, e cosa stampare per l'ispettore.
          </div>
        </div>
      </div>

    </GuidaLayout>
  );
}
